/* Armor builder for Cybertruck (sections/ao-armor-builder.liquid).

   Ported 2026-10-10 from the prototype in prototypes/armor-configurator/
   (src.html), which stays as the design reference; this file is the source
   from now on. What changed for the store:
   - prices, stock and variants come from the products picked in the section
     (window.AOArmorConfig.variants), in the shopper's own currency;
   - the renders load as they're needed (assets/ao-armor-meta.js, then one
     assets/ao-armor-set-*.js per view or turnaround frame);
   - Add to cart puts everything in the cart in one request and opens the
     theme's cart drawer; the discounts themselves are Shopify automatic
     discounts that mirror RULES below (10/15/25% off 3/4/5+ parts, 30% off
     the Ultimate Kit's add-ons);
   - everything is scoped to the builder's own element (.ao-armor), and its
     element ids carry an aoa- prefix. */
(() => {
'use strict';
const root = document.querySelector('.ao-armor');
if (!root) return;
const CFG = window.AOArmorConfig || {};

// Product photos and the two "which one do I have?" guides, from the store's Files.
const FILES = 'https://cdn.shopify.com/s/files/1/0424/5582/6596/files/';
const PHOTOS = {
  'front-bumper': 'cornercloseup_1f18213b-c8d5-40ab-9fae-e931832e45a0.png?v=1757709243',
  'rear-bumper': 'Upperdetail.png?v=1758905084',
  'flares': 'CameraDetail.png?v=1757708206',
  'rockers': 'wideshot_55aaa5a7-8654-4ed1-9151-ff7e96888122.png?v=1757709243',
  'terrestrial': 'front_long_shot_scenic.png?v=1759615440',
  'doors': 'tightshot_880755f0-d985-4cca-b930-95ca36407ba9.png?v=1759618242',
  'mirrors': 'Fullcoveragewide.png?v=1757709243',
  'mudflaps': 'rearangled.png?v=1733595600',
  'wheels': 'closeup_633c14c1-8829-4921-8186-7e7d52b16d8a.png?v=1757708206',
  'roof': 'CT-ARM-ROOFTRM-ezgif.com-cut.gif?v=1749245528',
  'baffle': 'Coveragearea.jpg?v=1784766453',
  'bed': 'Overheadside.png?v=1757709258',
  'vault-walls': 'Sunshotwithdepth.png?v=1757708206',
  'sail': 'Sidetrimoverhead.png?v=1745425006',
  'tailgate': 'sideshot_fada5035-3deb-454f-bc9d-ef832fcfcb3e.png?v=1757709258',
  'sills': 'Rear_d2dbaf66-27b9-4a29-96cf-2688842cbe50.png?v=1757708299',
  'door-panels': 'Frontalt.png?v=1757708206',
  'door-inserts': 'shot1.png?v=1757708299',
  'frunk-lip': 'interiorshot.png?v=1731788724',
  'frunk': 'beautyshot.png?v=1761514894',
};
const GUIDES = {
  'guide-roof': 'Access_panels_50f098ec-4936-4b8f-aebc-44ffe2f6f17f.png?v=1757708206',
  'guide-vault': 'Front_Wall_changes.png?v=1757709258',
};
const IMG = {};
for (const [k, f] of Object.entries(PHOTOS)) IMG[k] = `${FILES}${f}&width=360&height=270&crop=center`;
for (const [k, f] of Object.entries(GUIDES)) IMG[k] = `${FILES}${f}&width=420`;

/* ------------------------------------------------------------------ data */
// Variant IDs from the store; the prices here are 2026-10-08's and are
// replaced by the live ones below.

const PIECES = [
  { id: 'front-bumper', view: 'side', name: 'Front bumper', sub: 'The whole front bumper',
    blurb: 'Covers the whole front bumper and carries on the look of the wheel arches.',
    ease: 'Tricky', time: [30, 30], guide: 'install-xpel-armor-for-front-bumper-cybertruck',
    opts: [{ id: 'std', price: 165, zones: ['all'], vid: '43845325947053' }] },
  { id: 'rear-bumper', view: 'side', name: 'Rear bumper', sub: 'Choose how much to cover', def: 'full',
    blurb: 'The top surfaces take the wear when you stand on the bumper to load the bed. Go bigger to cover the lower face and the underside too.',
    ease: 'Easy', time: [30, 45], guide: 'install-xpel-armor-for-rear-bumper-cybertruck',
    opts: [
      { id: 'top', label: 'Top surfaces', price: 82, zones: ['top'], vid: '43822751121581' },
      { id: 'lower', label: 'Lower surface', price: 145, zones: ['lower'], vid: '43822751154349' },
      { id: 'full', label: 'Top & lower', price: 200, zones: ['top', 'lower'], vid: '43907701309613' },
      { id: 'extreme', label: 'Top, lower & underside', price: 265, zones: ['top', 'lower', 'under'], vid: '44946187845805' },
    ] },
  { id: 'flares', view: 'side', name: 'Wheel arches', sub: 'All four fender flares',
    blurb: 'All four wheel-arch flares. Far easier than regular paint protection film: no tools beyond the squeegee in the box.',
    ease: 'Easy', time: [45, 60], guide: 'install-xpel-armor-for-wheel-flares-cybertruck',
    opts: [{ id: 'std', price: 210, zones: ['all'], vid: '43771661058221' }] },
  { id: 'rockers', view: 'side', name: 'Rockers', sub: 'The long trims under the doors', def: 'std',
    blurb: 'Left and right rocker panels. They are long pieces, so take your time and watch the video first.',
    ease: 'Easy', time: [30, 30], guide: 'install-xpel-armor-for-rocker-panels-cybertruck',
    opts: [
      { id: 'std', label: 'Standard rockers', price: 145, zones: ['std'], vid: '43910340182189' },
      { id: 'terr', label: 'Terrestrial Armor Package', price: 105, zones: ['terr'], vid: '44997647728813', addon: true,
        img: 'terrestrial', time: [10, 15], guide: 'install-xpel-armor-for-the-terrestrial-armor-package-cybertruck',
        note: 'For trucks with Tesla’s Terrestrial Armor Package: eight pieces for its plastic upper rocker trims.' },
    ] },
  { id: 'doors', view: 'side', name: 'Lower doors', sub: 'The lower third of all four doors',
    blurb: 'Striking contrast against the stainless steel. Each piece wraps around the door edge so it lasts.',
    ease: 'Easy', time: [10, 15], guide: 'install-xpel-armor-for-the-cybertruck-doors-lower-thirds',
    opts: [{ id: 'std', price: 165, zones: ['all'], vid: '44997910921389', addon: true }] },
  { id: 'mirrors', view: 'side', name: 'Side mirrors', sub: 'Both mirror housings',
    blurb: 'A little trickier than the rest because of the curves. Still very doable: watch the video and fit them in the sun.',
    ease: 'Challenging', time: [15, 30], guide: 'install-xpel-armor-for-mirrors-cybertruck',
    opts: [{ id: 'std', price: 65, zones: ['all'], vid: '44140271141037' }] },
  { id: 'mudflaps', view: 'side', name: 'Mudflaps', sub: 'The faces of Tesla’s mudflaps',
    blurb: 'Finishes the look next to the wheel arches and rockers.',
    fit: 'Fits Tesla’s own mudflaps only. Third-party mudflaps vary in size.',
    ease: 'Easy', time: [10, 15], guide: 'install-armor-mudflap-decals-cybertruck',
    opts: [{ id: 'std', price: 45, zones: ['all'], vid: '43954821398701' }] },
  { id: 'wheels', view: 'side', name: 'Wheel decals', sub: 'The spokes of the all-terrain (Mantle) wheels',
    blurb: 'Adds texture to the flat faces of the wheel spokes. It is a near-perfect color match, so the effect is subtle. 28 pieces, 7 per wheel.',
    fit: 'Fits the all-terrain (Mantle) wheels only.',
    ease: 'Easy', time: [30, 45], guide: 'install-xpel-armor-all-terrain-wheel-decals-cybertruck',
    opts: [{ id: 'std', price: 65, zones: ['all'], vid: '43805482614957' }] },
  { id: 'roof', view: 'top', name: 'Roof trims', sub: 'Either side of the glass roof', truck: 'roof',
    blurb: 'The piano-black roof trims scratch very easily, especially if you use roof bars.',
    ease: 'Easy', time: [10, 15], guide: 'install-xpel-armor-for-roof-trim-cybertruck',
    opts: [{ id: 'std', price: 55, zones: ['all'], vid: { cutouts: '44677817041069', nocutouts: '44677817073837' } }] },
  { id: 'bed', view: 'top', name: 'Bed floor', sub: 'The whole floor of the bed',
    blurb: 'Turns the dusty grey factory floor into a tough black textured finish. A spray-on liner costs $1,500 or more and keeps the truck off the road for a day or two.',
    ease: 'Challenging', time: [60, 75], guide: 'install-xpel-armor-for-truckbed-cybertruck',
    opts: [{ id: 'std', price: 455, zones: ['all'], vid: '44366162067629' }] },
  { id: 'vault-walls', view: 'top', name: 'Vault walls', sub: 'The side and front walls of the bed', truck: 'vault', def: 'full',
    blurb: 'The vault walls are almost impossible to keep looking good. These are large pieces, so watch the video before you start.',
    ease: 'Challenging', time: [30, 45], guide: 'install-xpel-armor-for-vault-panels-cybertruck',
    opts: [
      { id: 'full', label: 'Full coverage', price: 325, zones: ['lower', 'upper'], vid: { flat: '43954083954861', ribbed: '44705687044269' } },
      { id: 'lower', label: 'Lower coverage', price: 270, zones: ['lower'], vid: { flat: '43954083987629', ribbed: '44705687077037' } },
    ] },
  { id: 'sail', view: 'top', name: 'Bed rails & tailgate lip', sub: 'The trims around the top of the bed', def: 'full',
    blurb: 'The sail trims along both sides of the bed, and the lip of the tailgate.',
    ease: 'Tricky', time: [30, 30], guide: 'install-xpel-armor-for-bed-trim-cybertruck',
    opts: [
      { id: 'lip', label: 'Tailgate lip only', price: 70, zones: ['lip'], vid: '43679334465709' },
      { id: 'full', label: 'Full set, 4 pieces', price: 205, zones: ['lip', 'rails'], vid: '43679334498477' },
    ] },
  { id: 'tailgate', view: 'top', name: 'Tailgate (inside)', sub: 'The inner face of the tailgate',
    blurb: 'Pairs with the vault walls. The most intricate piece in the range, so watch the video first: the method in it took many hours to work out.',
    ease: 'Challenging', time: [30, 45], guide: 'install-xpel-armor-for-tailgate-interior-cybertruck',
    opts: [{ id: 'std', price: 130, zones: ['all'], vid: '44275099828397' }] },
  { id: 'sills', view: 'cabin', name: 'Door sills', sub: 'All four door sills',
    blurb: 'There is no real step into a Cybertruck, so shoes scuff the sills. This protects them.',
    ease: 'Easy', time: [10, 15], guide: 'install-xpel-armor-sill-protection-cybertruck',
    opts: [{ id: 'std', price: 40, zones: ['all'], vid: '43598701035693' }] },
  { id: 'door-panels', view: 'cabin', name: 'Lower door panels', sub: 'The kick area inside each door',
    blurb: 'Protects the bottom of each door card inside the cabin and makes it easy to wipe clean.',
    ease: 'Tricky', time: [15, 30], guide: 'install-xpel-armor-for-lower-door-panels-cybertruck',
    opts: [{ id: 'std', price: 60, zones: ['all'], vid: '44037574262957' }] },
  { id: 'door-inserts', view: 'cabin', name: 'Door shelf inserts', sub: 'The shelf on each front door',
    blurb: 'Tap the front door shelf and it sounds hollow. These two pieces fix the look and add protection.',
    ease: 'Easy', time: [10, 15], guide: 'install-xpel-armor-door-inserts-cybertruck',
    opts: [{ id: 'std', price: 10, zones: ['all'], vid: '43805420978349' }] },
  { id: 'baffle', view: 'cabin', name: 'Baffle trims', sub: 'Behind each rear door opening',
    blurb: 'The plastic panels between the rear door opening and the rear quarter panel. They tend to fade to an ashy grey.',
    ease: 'Easy', time: [10, 15], guide: 'install-xpel-armor-baffle-trim-cybertruck',
    opts: [{ id: 'std', price: 55, zones: ['all'], vid: '46891294916781', addon: true }] },
  { id: 'frunk-lip', view: 'frunk', name: 'Frunk lip', sub: 'The front edge of the frunk',
    blurb: 'The plastic lip at the front of the frunk, which scratches easily.',
    ease: 'Easy', time: [10, 15], guide: 'install-armor-protection-for-frunk-lip-cybertruck',
    opts: [{ id: 'std', price: 32, zones: ['all'], vid: '43937390100653' }] },
  { id: 'frunk', view: 'frunk', name: 'Frunk interior', sub: 'Walls, or walls and floor', def: 'full',
    blurb: 'Choose walls only if you already have a frunk liner. Includes alternative pieces if you have an L-track system.',
    ease: 'Tricky', time: [10, 15], guide: 'install-xpel-armor-for-frunk-cybertruck',
    opts: [
      { id: 'walls', label: 'Walls only', price: 105, zones: ['walls'], vid: '45146222493869', addon: true },
      { id: 'full', label: 'Walls & floor', price: 165, zones: ['walls', 'floor'], vid: '45146222526637', addon: true },
    ] },
];
const PIECE = Object.fromEntries(PIECES.map(p => [p.id, p]));

// Sold separately for owners of the full rear bumper kit. Top & lower plus
// this add-on covers the same as "Top, lower & underside".
const REAR_ADDON = { pid: 'rear-bumper', oid: 'addon', label: 'Rear bumper add-on', price: 95, zones: ['under'], addon: true, extra: true, vid: '44940297273517' };

const INSTALL_KIT = { price: 30, vid: '44412188655789' };

const KITS = [
  { id: 'ultimate', name: 'Ultimate Kit', livePrice: 1500, prep: true, unlocks: true, img: 'bed', view: 'side',
    cov: 'The whole truck: outside, bed, cabin, wheels and roof',
    sub: '16 pieces: everything in the Exterior, Vault and Interior kits, plus wheel decals and roof trims. Installation kit included.',
    covers: { 'front-bumper': ['all'], 'rear-bumper': ['top', 'lower'], flares: ['all'], rockers: ['std'], mirrors: ['all'],
      mudflaps: ['all'], wheels: ['all'], roof: ['all'], bed: ['all'], 'vault-walls': ['lower', 'upper'], sail: ['lip', 'rails'],
      tailgate: ['all'], sills: ['all'], 'door-panels': ['all'], 'door-inserts': ['all'], 'frunk-lip': ['all'] },
    needs: ['roof', 'vault'], vids: ['44693655617709', '44705738490029', '44693655650477', '44705738522797'],
    vid: a => ({ 'cutouts|flat': '44693655617709', 'cutouts|ribbed': '44705738490029', 'nocutouts|flat': '44693655650477', 'nocutouts|ribbed': '44705738522797' })[`${a.roof}|${a.vault}`] },
  { id: 'exterior', name: 'Exterior Kit', livePrice: 650, prep: true, img: 'flares', view: 'side',
    cov: 'Bumpers, rockers, wheel arches, mirrors, mudflaps',
    sub: 'Both bumpers, rockers, wheel arches, mirrors and mudflaps. Installation kit included.',
    covers: { 'front-bumper': ['all'], 'rear-bumper': ['top', 'lower'], rockers: ['std'], flares: ['all'], mirrors: ['all'], mudflaps: ['all'] },
    needs: [], vids: ['44401311285421'], vid: () => '44401311285421' },
  { id: 'vault', name: 'Vault Kit', livePrice: 899, prep: true, img: 'vault-walls', view: 'top',
    cov: 'Bed floor, vault walls, tailgate, bed rails',
    sub: 'Bed floor, vault walls, inside of the tailgate, bed rails and tailgate lip. Installation kit included.',
    covers: { 'vault-walls': ['lower', 'upper'], tailgate: ['all'], bed: ['all'], sail: ['lip', 'rails'] },
    needs: ['vault'], vids: ['44705651982509', '44705651949741'], vid: a => (a.vault === 'ribbed' ? '44705651982509' : '44705651949741') },
  { id: 'interior', name: 'Interior Kit', livePrice: 130, prep: true, img: 'sills', view: 'cabin',
    cov: 'Door sills, door panels, shelf inserts, frunk lip',
    sub: 'Door sills, lower door panels, door shelf inserts and frunk lip. Comes with a small alcohol spray, cloth and squeegee.',
    covers: { sills: ['all'], 'door-panels': ['all'], 'door-inserts': ['all'], 'frunk-lip': ['all'] },
    needs: [], vids: ['44403131580589'], vid: () => '44403131580589' },
];

// The store's rules. Checkout charges these through Shopify automatic
// discounts ("Cybertruck Armor bundle: 10% off 3 parts", 15% off 4, 25% off 5+,
// and "Ultimate Kit: 30% off N add-ons" for N = 1 to 5), listed in
// prototypes/armor-configurator/discounts.json; change one, change both.
const RULES = { t3: 10, t4: 15, t5: 25, addon: 30 };

// Live prices and stock from the products in the section's settings. A
// variant the page wasn't given counts as unavailable rather than selling at
// a remembered price.
const LIVE = CFG.variants || null;
const vidList = vid => typeof vid === 'string' ? [vid] : Object.values(vid);
const liveOf = vid => LIVE ? vidList(vid).map(v => LIVE[v]).find(Boolean) : null;
const isOut = vid => !!LIVE && vidList(vid).every(v => !LIVE[v] || !LIVE[v].a);
if (LIVE) {
  for (const p of PIECES) for (const o of p.opts) {
    const l = liveOf(o.vid);
    if (l) o.price = l.p / 100;
    o.out = isOut(o.vid);
  }
  for (const x of [REAR_ADDON, INSTALL_KIT]) {
    const l = liveOf(x.vid);
    if (l) x.price = l.p / 100;
    x.out = isOut(x.vid);
  }
  for (const k of KITS) {
    const l = liveOf(k.vids);
    if (l) k.livePrice = l.p / 100;
    k.out = isOut(k.vids);
  }
}

const TIER_STEPS = [3, 4, 5];
const tierPct = n => n >= 5 ? RULES.t5 : n === 4 ? RULES.t4 : n === 3 ? RULES.t3 : 0;
const tierRate = n => tierPct(n) / 100;

const QUESTIONS = {
  vault: {
    legend: 'Your vault front wall',
    options: [
      { id: 'flat', label: 'Flat', note: 'Every Cyberbeast, and any truck built before March 17, 2025' },
      { id: 'ribbed', label: 'Ribbed', note: 'Dual-motor trucks built from March 17, 2025' },
    ],
    img: 'guide-vault', imgAlt: 'The original flat vault front wall above the newer ribbed one',
  },
  roof: {
    legend: 'Your roof trims',
    options: [
      { id: 'cutouts', label: 'With access panels', note: 'All Foundation Series and Cyberbeast trucks' },
      { id: 'nocutouts', label: 'No access panels', note: 'Some dual-motor and Long Range trucks from about May 2025' },
    ],
    img: 'guide-roof', imgAlt: 'Roof trims from above, with the four small access panels circled',
  },
};

/* ---------------------------------------------------------------- helpers */
const r2 = x => Math.round(x * 100) / 100;
const sumP = list => list.reduce((s, x) => s + x.price, 0);
const moneyFormats = (() => {
  const make = (loc, d) => new Intl.NumberFormat(loc, { style: 'currency', currency: CFG.currency || 'USD', minimumFractionDigits: d, maximumFractionDigits: d });
  try { return [make(CFG.locale || 'en-US', 0), make(CFG.locale || 'en-US', 2)]; } catch (e) { return [make('en-US', 0), make('en-US', 2)]; }
})();
const money = n => {
  const v = r2(n);
  return moneyFormats[Math.abs(v - Math.round(v)) > 0.001 ? 1 : 0].format(v);
};
const joinNames = arr => arr.length <= 1 ? (arr[0] || '') : arr.slice(0, -1).join(', ') + ' and ' + arr[arr.length - 1];
const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
const optOf = (pid, oid) => PIECE[pid].opts.find(o => o.id === oid) || PIECE[pid].opts[0];
// The default option, or the first one in stock if the default isn't.
const defOpt = pid => {
  const p = PIECE[pid], d = p.def || p.opts[0].id;
  return optOf(pid, d).out ? (p.opts.find(o => !o.out) || optOf(pid, d)).id : d;
};
const pieceOut = pid => PIECE[pid].opts.every(o => o.out);
const skuName = s => s.extra ? s.label : PIECE[s.pid].name + (PIECE[s.pid].opts.length > 1 ? ` (${optOf(s.pid, s.oid).label})` : '');
const priceFrom = p => Math.min(...p.opts.filter(o => !o.out).map(o => o.price));

/* ----------------------------------------------------------------- pricing */
function skusFor(pid) {
  const list = PIECE[pid].opts.filter(o => !o.out).map(o => ({ pid, oid: o.id, price: o.price, zones: o.zones, addon: !!o.addon }));
  if (pid === 'rear-bumper' && !REAR_ADDON.out) list.push(REAR_ADDON);
  return list;
}

// Cheapest set of this piece's products that covers the zones still needed.
function cheapestCover(pid, need) {
  const list = skusFor(pid);
  let best = null;
  for (let m = 1; m < (1 << list.length); m++) {
    const pick = list.filter((_, i) => m & (1 << i));
    const zones = new Set(pick.flatMap(s => s.zones));
    if (!need.every(z => zones.has(z))) continue;
    const cost = sumP(pick);
    if (!best || cost < best.cost - 1e-9 || (Math.abs(cost - best.cost) < 1e-9 && pick.length < best.pick.length)) best = { cost, pick };
  }
  return best;
}

// What a kit's contents cost bought one by one today (installation kit included where the kit includes it).
function kitParts(k) {
  let t = k.prep && k.id !== 'interior' ? INSTALL_KIT.price : 0;
  for (const [pid, zs] of Object.entries(k.covers)) t += cheapestCover(pid, zs).cost;
  return t;
}
const kitPrice = k => k.livePrice;

// Try every combination of kits, bundle and add-on pricing; keep the cheapest.
function solve(sel, withPrep) {
  const pids = Object.keys(sel);
  if (!pids.length) return null;
  const need = {};
  for (const pid of pids) need[pid] = optOf(pid, sel[pid]).zones;
  let baseline = withPrep ? INSTALL_KIT.price : 0;
  for (const pid of pids) baseline += cheapestCover(pid, need[pid]).cost;
  const addRate = RULES.addon / 100;

  let best = null;
  for (let m = 0; m < (1 << KITS.length); m++) {
    const kits = KITS.filter((_, i) => m & (1 << i));
    if (kits.some(k => k.out)) continue;
    if (kits.some(k => !pids.some(pid => (k.covers[pid] || []).some(z => need[pid].includes(z))))) continue;
    const cov = {};
    for (const k of kits) for (const [pid, zs] of Object.entries(k.covers)) {
      cov[pid] = cov[pid] || new Set();
      zs.forEach(z => cov[pid].add(z));
    }
    const items = [];
    for (const pid of pids) {
      const rem = need[pid].filter(z => !(cov[pid] && cov[pid].has(z)));
      if (rem.length) items.push(...cheapestCover(pid, rem).pick);
    }
    const kitCost = kits.reduce((s, k) => s + kitPrice(k), 0);
    const prepCost = withPrep && !kits.some(k => k.prep) ? INSTALL_KIT.price : 0;
    const elig = kits.some(k => k.unlocks) ? items.filter(s => s.addon) : [];
    const rest = items.filter(s => !elig.includes(s));
    const extra = kits.reduce((n, k) => n + Object.keys(k.covers).filter(pid => !sel[pid]).length, 0);
    for (let a = 0; a < (1 << elig.length); a++) {
      const add = elig.filter((_, i) => a & (1 << i));
      const pool = rest.concat(elig.filter((_, i) => !(a & (1 << i))));
      const rate = tierRate(pool.length);
      const poolList = sumP(pool), addList = sumP(add);
      const poolCost = r2(poolList * (1 - rate));
      const addCost = r2(addList * (1 - addRate));
      const total = r2(kitCost + poolCost + addCost + prepCost);
      const better = !best || total < best.total - 0.005 ||
        (Math.abs(total - best.total) <= 0.005 && (extra > best.extra || (extra === best.extra && kits.length < best.kits.length)));
      if (better) best = { total, kits, pool, rate, poolList, poolCost, add, addList, addCost, prepCost, cov, extra };
    }
  }
  best.baseline = r2(baseline);
  best.saving = r2(baseline - best.total);
  best.need = need;
  return best;
}

// Pieces the customer did not pick but gets anyway, because a kit includes them.
function kitExtras(sel, s) {
  if (!s) return [];
  const out = [];
  for (const k of s.kits) for (const pid of Object.keys(k.covers)) if (!sel[pid] && !out.includes(pid)) out.push(pid);
  return out;
}

// Single parts the customer could add, cheapest effect on the total first.
function addCandidates(sel, s, withPrep) {
  if (!s) return [];
  const covered = new Set(Object.keys(s.cov));
  return PIECES.filter(p => !sel[p.id] && !covered.has(p.id)).map(p => {
    const oid = defOpt(p.id);
    const b2 = solve({ ...sel, [p.id]: oid }, withPrep);
    return { pid: p.id, oid, list: optOf(p.id, oid).price, delta: r2(b2.total - s.total) };
  }).sort((a, b) => a.delta - b.delta || a.list - b.list);
}

// Completing a kit the customer is already most of the way to.
function kitNudge(sel, s, withPrep) {
  if (!s) return null;
  const covered = new Set(Object.keys(s.cov));
  const cands = [];
  for (const k of KITS) {
    if (s.kits.includes(k)) continue;
    const kp = Object.keys(k.covers);
    if (kp.filter(pid => sel[pid]).length < Math.ceil(kp.length / 2)) continue;
    const missing = kp.filter(pid => !sel[pid] && !covered.has(pid));
    if (!missing.length || missing.length > 4) continue;
    const picks = {};
    for (const pid of missing) picks[pid] = defOpt(pid);
    const b2 = solve({ ...sel, ...picks }, withPrep);
    const list = missing.reduce((t, pid) => t + optOf(pid, picks[pid]).price, 0);
    const delta = r2(b2.total - s.total);
    if (delta <= list * 0.6) cands.push({ kit: k, pids: missing, picks, list, delta, value: list - delta, kitWins: b2.kits.includes(k) });
  }
  cands.sort((a, b) => b.value - a.value);
  return cands[0] || null;
}

// Where the bundle stands and what the next step is worth.
function tierInfo(s) {
  if (!s || !s.pool.length) return null;
  const n = s.pool.length;
  const next = TIER_STEPS.find(t => t > n && tierPct(t) > tierPct(n));
  if (!next) return { n, done: true, pct: tierPct(n) };
  const need = next - n;
  const pct = tierPct(next);
  const extra = r2(s.poolList * (tierRate(next) - s.rate));
  return { n, need, next, pct, extra, cur: tierPct(n) };
}

function fitTime(pids) {
  let lo = 0, hi = 0;
  for (const pid of pids) {
    const o = state.sel[pid] ? optOf(pid, state.sel[pid]) : null;
    const t = (o && o.time) || PIECE[pid].time;
    lo += t[0]; hi += t[1];
  }
  return [lo, hi];
}
function timeText([lo, hi]) {
  if (!hi) return '';
  if (hi < 60) return `About ${lo} to ${hi} minutes to fit.`;
  const h = m => { const v = Math.round(m / 30) / 2; return Math.floor(v) + (v % 1 ? '½' : ''); };
  const a = h(lo), b = h(hi);
  return a === b ? `About ${a} hours to fit.` : `About ${a} to ${b} hours to fit, piece by piece.`;
}

/* ---------------------------------------------------------------- renders */
// Studio renders of HKV Studios' 2025 Cybertruck (render/views.py). Each view
// has a clean truck and one transparent layer per Armor part, cropped to the
// part; showing a layer "armors" that part. hit = which part is under each
// cell of a 400x225 grid, run-length encoded, for taps and hovers.
//
// On the store each image set is its own file (assets/ao-armor-set-*.js),
// which registers itself in window.AOArmorSets and announces itself with an
// "aoarmor:set" event. ao-armor-meta.js says up front which views exist, the
// turnaround's angles, part areas, hotspots and the view thumbnails.
const META = window.AOArmorMeta || { views: {}, spin: null, thumbs: {} };
const SETS = window.AOArmorSets = window.AOArmorSets || {};
const RENDERS = { views: {} };
for (const [id, m] of Object.entries(META.views)) RENDERS.views[id] = { key: id, from: m.from };
const asked = new Set();
function requestSet(key) {
  if (SETS[key] || asked.has(key) || !CFG.sets || !CFG.sets[key]) return;
  asked.add(key);
  const el = document.createElement('script');
  el.src = CFG.sets[key];
  el.async = true;
  document.head.appendChild(el);
}
const whenSet = key => SETS[key] ? Promise.resolve() : new Promise(done => {
  const on = e => { if (e.detail === key) { window.removeEventListener('aoarmor:set', on); done(); } };
  window.addEventListener('aoarmor:set', on);
  requestSet(key);
});

function toBlobURL(uri) {
  try {
    const [head, b64] = uri.split(',');
    const bin = atob(b64), buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([buf], { type: head.slice(5, head.indexOf(';')) }));
  } catch (e) { return uri; }
}
// Unpack an image set (a view, or one frame of the turnaround) the first time
// it is needed: blob URLs, the tap grid, and its own image elements, kept so
// that turning the truck swaps ready-made, decoded pictures.
function ready(set) {
  if (!set || set._ready) return set;
  if (!SETS[set.key]) { requestSet(set.key); return null; }
  Object.assign(set, SETS[set.key]);
  if (set.base.startsWith('data:')) set.base = toBlobURL(set.base);
  for (const l of Object.values(set.layers)) if (l.src.startsWith('data:')) l.src = toBlobURL(l.src);
  const grid = new Uint8Array(set.hit.w * set.hit.h);
  for (let i = 0, p = 0; i < set.hit.rle.length; i += 2) { grid.fill(set.hit.rle[i], p, p + set.hit.rle[i + 1]); p += set.hit.rle[i + 1]; }
  set.grid = grid;
  const pos = l => `left:${(l.x / set.w * 100).toFixed(3)}%;top:${(l.y / set.h * 100).toFixed(3)}%;width:${(l.w / set.w * 100).toFixed(3)}%;height:${(l.h / set.h * 100).toFixed(3)}%`;
  const img = (cls, src, style) => {
    const el = document.createElement('img');
    el.className = cls; el.src = src; el.alt = ''; el.draggable = false;
    if (style) el.style.cssText = style;
    if (el.decode) el.decode().catch(() => {});
    return el;
  };
  const els = { base: img('base', set.base), layers: {}, hls: {}, boxes: [] };
  // A part's zones share a box the size of all of them together, so that
  // picking two that meet (frunk walls and floor) draws one outline, not a
  // seam between them.
  const byPart = {};
  for (const [k, l] of Object.entries(set.layers)) (byPart[k.split('.')[0]] ||= []).push([k, l]);
  for (const ls of Object.values(byPart)) {
    const x0 = Math.min(...ls.map(([, l]) => l.x)), y0 = Math.min(...ls.map(([, l]) => l.y));
    const w = Math.max(...ls.map(([, l]) => l.x + l.w)) - x0, h = Math.max(...ls.map(([, l]) => l.y + l.h)) - y0;
    const box = document.createElement('div');
    box.className = 'part-box';
    box.style.cssText = pos({ x: x0, y: y0, w, h });
    for (const [k, l] of ls) {
      const el = img('layer', l.src, `left:${((l.x - x0) / w * 100).toFixed(3)}%;top:${((l.y - y0) / h * 100).toFixed(3)}%;width:${(l.w / w * 100).toFixed(3)}%;height:${(l.h / h * 100).toFixed(3)}%;opacity:0`);
      el.dataset.k = k;
      els.layers[k] = el;
      box.append(el);
    }
    els.boxes.push(box);
  }
  for (const [k, l] of Object.entries(set.layers)) {
    const hl = document.createElement('div');
    hl.className = 'hl';
    hl.style.cssText = `${pos(l)};-webkit-mask-image:url(${l.src});mask-image:url(${l.src})`;
    hl.addEventListener('animationend', () => hl.classList.remove('flash'));
    els.hls[k] = hl;
  }
  // The opening animation, for the views that open something.
  if (set.open) {
    set.open = set.open.map(u => u.startsWith('data:') ? toBlobURL(u) : u);
    els.open = set.open.map(u => img('base', u));
  }
  set.els = els;
  set._ready = true;
  return set;
}

// Drag-to-turn: frames of the closed truck all the way round, each with its
// own Armor layers. Frame 0 now, the rest a few at a time once the page is up.
const SPIN = META.spin && META.spin.count ? { ...META.spin,
  frames: Array.from({ length: META.spin.count }, (_, i) => ({ key: `spin/${String(i).padStart(2, '0')}` })) } : null;
if (SPIN) {
  // Frame numbers run round the truck one way; dragging right should carry
  // the side nearest you to the right.
  SPIN.dir = -1;
  ready(SPIN.frames[0]);
}
// Once the page has loaded: the open views (for the hotspots and the
// thumbnails), then the rest of the turnaround, two files at a time, each
// unpacked as it lands so turning the truck stays smooth.
function warmUp() {
  const sets = [...Object.values(RENDERS.views), ...(SPIN ? SPIN.frames.slice(1) : [])];
  let i = 0;
  const next = () => {
    const set = sets[i++];
    if (!set) return;
    whenSet(set.key).then(() => { ready(set); setTimeout(next, 20); });
  };
  next(); next();
}
if (document.readyState === 'complete') setTimeout(warmUp, 400);
else window.addEventListener('load', () => setTimeout(warmUp, 400));

// Thumbnails for the first four; the last two are reached from a part or a
// hotspot, and light up the thumbnail named in 'thumb'.
const VIEWS = [
  { id: 'outside', label: 'Outside', name: 'Outside' },
  { id: 'bed', label: 'Bed', name: 'Bed, tailgate down' },
  { id: 'cabin', label: 'Doors', name: 'Doors open' },
  { id: 'frunk', label: 'Frunk', name: 'Frunk open' },
  { id: 'front', label: 'Doors', name: 'Doors open, from the front', hidden: true, thumb: 'cabin' },
  { id: 'under', label: 'Underneath', name: 'Under the rear bumper', hidden: true, thumb: 'outside' },
];
const THUMBS = VIEWS.filter(v => !v.hidden);
const thumbOf = id => (VIEWS.find(v => v.id === id) || {}).thumb || id;
const SECTIONS = [
  { id: 'kits', title: 'Start with a kit', view: 'outside' },
  { id: 'outside', title: 'Outside', view: 'outside', parts: ['front-bumper', 'flares', 'rockers', 'doors', 'mirrors', 'mudflaps', 'wheels', 'rear-bumper'] },
  { id: 'bed', title: 'Bed and roof', view: 'bed', parts: ['bed', 'vault-walls', 'sail', 'tailgate', 'roof'] },
  { id: 'cabin', title: 'Cabin', view: 'cabin', parts: ['sills', 'door-panels', 'door-inserts', 'baffle'] },
  { id: 'frunk', title: 'Frunk', view: 'frunk', parts: ['frunk-lip', 'frunk'] },
];
// The view where each part shows best; picking a part turns the truck to it.
const BEST = {
  'front-bumper': 'outside', 'rear-bumper': SPIN ? 'outside' : 'bed', flares: 'outside', rockers: 'outside', doors: 'outside',
  mirrors: 'outside', mudflaps: 'outside', wheels: 'outside', roof: 'bed', bed: 'bed', 'vault-walls': 'bed',
  sail: 'bed', tailgate: 'bed', sills: 'cabin', 'door-panels': 'cabin', 'door-inserts': 'cabin', baffle: 'front',
  'frunk-lip': 'frunk', frunk: 'frunk',
};
const layerKeys = (pid, zones) => pid === 'rockers' ? ['rockers.std'] : zones.map(z => `${pid}.${z}`);
const allZones = pid => [...new Set(PIECE[pid].opts.flatMap(o => o.zones))];

/* ------------------------------------------------------------------ state */
const state = {
  sel: {},
  view: 'outside',
  frame: 0,
  shut: {},        // sections folded away
  answers: { vault: null, roof: null },
  terrSwap: false,  // a kit's standard rockers to be swapped for the Terrestrial kit
  prep: true,
  open: null,
  hover: null,
  cart: false,
  cartErr: '',
  sheet: false,
  keepOpen: {},
};
let sol = null, tier = null, nudge = null, adds = [];
let manualUntil = 0;
const $ = id => document.getElementById('aoa-' + id);
const isWide = () => matchMedia('(min-width: 1000px)').matches;

/* ------------------------------------------------------------- the truck */
let viewEls = { layers: {}, hls: {}, boxes: [] };
let dragged = false;   // once someone has turned the truck, the hint goes

// The pictures on screen: a turnaround frame on Outside, else the fixed view.
const spinning = () => state.view === 'outside' && !!SPIN;
const wanted = () => spinning() ? SPIN.frames[state.frame] : RENDERS.views[state.view];
const cur = () => ready(wanted());
// A set arriving that's the one on screen: draw it.
window.addEventListener('aoarmor:set', e => {
  const w = wanted();
  if (w && w.key === e.detail && !busy) { buildView(); paintTruck(); }
});

function buildView() {
  const v = cur();
  const name = VIEWS.find(x => x.id === state.view).name;
  $('viewName').textContent = name;
  $('frame').classList.toggle('spin', spinning());
  $('dragHint').hidden = !spinning() || dragged;
  // Not here yet: keep the last picture, with a spinner, until it is.
  $('frame').classList.toggle('loading', !v);
  if (!v) { paintSpots(); return; }
  v.els.base.alt = `Cybertruck, ${name.toLowerCase()}`;
  $('base').replaceChildren(v.els.base);
  $('layers').replaceChildren(...v.els.boxes);
  $('hls').replaceChildren(...Object.values(v.els.hls));
  viewEls = { layers: v.els.layers, hls: v.els.hls, boxes: v.els.boxes };
  paintSpots();
}

function armoredKeys() {
  const keys = new Set();
  if (!sol) return keys;
  for (const [pid, oid] of Object.entries(state.sel)) layerKeys(pid, optOf(pid, oid).zones).forEach(k => keys.add(k));
  for (const [pid, zs] of Object.entries(sol.cov)) layerKeys(pid, [...zs]).forEach(k => keys.add(k));
  return keys;
}

function paintTruck() {
  const on = armoredKeys();
  for (const [k, el] of Object.entries(viewEls.layers)) el.style.opacity = on.has(k) ? 1 : 0;
  for (const box of viewEls.boxes) box.classList.toggle('sel', [...box.children].some(el => on.has(el.dataset.k)));
  const hk = state.hover ? new Set(layerKeys(state.hover, allZones(state.hover))) : null;
  // Armored parts keep a light orange wash so it's clear what the film covers.
  for (const [k, el] of Object.entries(viewEls.hls)) {
    el.classList.toggle('sel', on.has(k));
    el.classList.toggle('on', !!hk && hk.has(k));
  }
}

function flash(pid) {
  for (const k of layerKeys(pid, allZones(pid))) {
    const el = viewEls.hls[k];
    if (!el) continue;
    el.classList.remove('flash');
    void el.offsetWidth;
    el.classList.add('flash');
  }
}

function partAt(clientX, clientY) {
  const v = cur();
  if (!v) return null;
  const r = $('frame').getBoundingClientRect();
  const gx = Math.floor((clientX - r.left) / r.width * v.hit.w);
  const gy = Math.floor((clientY - r.top) / r.height * v.hit.h);
  if (gx < 0 || gy < 0 || gx >= v.hit.w || gy >= v.hit.h) return null;
  const idx = v.grid[gy * v.hit.w + gx];
  return idx ? v.hit.keys[idx - 1].split('.')[0] : null;
}

function renderThumbs() {
  $('thumbs').innerHTML = THUMBS.map(x => {
    const src = META.thumbs[x.id];
    return `<button class="thumb" role="tab" type="button" id="aoa-tab-${x.id}" data-view="${x.id}" aria-selected="${state.view === x.id}" tabindex="${state.view === x.id ? 0 : -1}">
      ${src ? `<img src="${src}" alt="">` : ''}<span>${x.label}</span><i hidden></i></button>`;
  }).join('');
}
function paintThumbs() {
  for (const x of THUMBS) {
    const t = $('tab-' + x.id);
    const on = thumbOf(state.view) === x.id;
    t.setAttribute('aria-selected', String(on));
    t.tabIndex = on ? 0 : -1;
    const n = Object.keys(state.sel).filter(pid => thumbOf(BEST[pid]) === x.id).length;
    const b = t.querySelector('i');
    b.textContent = n;
    b.hidden = !n;
  }
}

function setView(id, manual) {
  if (manual) manualUntil = Date.now() + 2500;
  if (state.view === id) return;
  state.view = id;
  state.hover = null;
  $('tip').hidden = true;
  buildView();
  paintTruck();
  paintThumbs();
}

let turnTimer = 0;
function showFrame(f) {
  state.frame = f;
  buildView();
  paintTruck();
}
function turnTo(target) {
  clearInterval(turnTimer);
  return new Promise(done => {
    if (state.frame === target || matchMedia('(prefers-reduced-motion: reduce)').matches) { showFrame(target); done(); return; }
    const n = SPIN.frames.length;
    turnTimer = setInterval(() => {
      if (state.frame === target || !spinning()) { clearInterval(turnTimer); done(); return; }
      const fwd = (target - state.frame + n) % n;
      showFrame((state.frame + (fwd <= n / 2 ? 1 : n - 1)) % n);
    }, 30);
  });
}
// The turnaround frame nearest an angle (0 = the front, 90 = the driver's side).
const nearestFrame = az => Math.round(((((az - SPIN.start) % 360) + 360) % 360) / SPIN.step) % SPIN.frames.length;
// For a part on the turnaround: the nearest frame that shows it well (at
// least 85% of its best), so picking a part turns the truck as little as it can.
function bestFrame(pid) {
  if (!SPIN) return null;
  const n = SPIN.frames.length;
  const rows = layerKeys(pid, allZones(pid)).map(k => SPIN.areas[k]).filter(Boolean);
  if (!rows.length) return null;
  const shown = Array.from({ length: n }, (_, i) => rows.reduce((t, r) => t + r[i], 0));
  const good = Math.max(...shown) * 0.85;
  let pick = null, far = n;
  for (let i = 0; i < n; i++) {
    const d = Math.min((i - state.frame + n) % n, (state.frame - i + n) % n);
    if (shown[i] >= good && d < far) { pick = i; far = d; }
  }
  return pick;
}

/* ------------------------------------------------- opening the truck */
// Hotspots on the turnaround open the frunk, the vault and the doors: the
// truck turns to that view's angle, then plays a few frames of it opening and
// lands on the open view. A "Close" chip there plays it backwards.
const OPENERS = {
  frunk: { to: ['frunk'], noun: 'frunk', sec: 'frunk' },
  vault: { to: ['bed'], noun: 'vault', sec: 'bed' },
  doors: { to: ['front', 'cabin'], noun: 'doors', sec: 'cabin' },
};
// The turnaround frame a view's animation starts from.
const fromFrame = id => {
  const v = RENDERS.views[id];
  return v && v.from != null ? v.from : 0;
};
const ringDist = (a, b) => { const n = SPIN.frames.length, d = Math.abs(a - b) % n; return Math.min(d, n - d); };
function targetOf(key) {
  const to = OPENERS[key].to.filter(id => RENDERS.views[id]);
  return to.sort((a, b) => ringDist(fromFrame(a), state.frame) - ringDist(fromFrame(b), state.frame))[0];
}
const openerOf = view => Object.keys(OPENERS).find(k => OPENERS[k].to.includes(view));
const PLUS = '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M7 2.5 V11.5 M2.5 7 H11.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
const CROSS = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
let busy = false;
const wait = ms => new Promise(r => setTimeout(r, ms));

function paintSpots() {
  const box = $('spots');
  if (busy) { box.replaceChildren(); return; }
  if (spinning()) {
    const at = (SPIN.hotspots && SPIN.hotspots[state.frame]) || {};
    box.innerHTML = Object.entries(at).filter(([, p]) => p).map(([k, [x, y]]) =>
      `<button class="spot${x > 0.62 ? ' flip' : ''}" type="button" data-open="${k}" style="left:${(x * 100).toFixed(2)}%;top:${(y * 100).toFixed(2)}%"><i>${PLUS}</i><span><em>Open </em>${OPENERS[k].noun}</span></button>`).join('');
    return;
  }
  if (!SPIN) { box.replaceChildren(); return; }
  if (state.view === 'under') {
    box.innerHTML = `<button class="spot-close" type="button" data-close="under">${CROSS}Back</button>`;
    return;
  }
  const k = openerOf(state.view);
  box.innerHTML = k ? `<button class="spot-close" type="button" data-close="${k}">${CROSS}Close ${OPENERS[k].noun}</button>` : '';
}

// Step through the opening frames and land on the open view; closing runs
// the same frames backwards and lands on the turnaround. The first frame
// follows on from the turnaround frame, so there is nothing to cross-fade.
async function play(frames, opening, land) {
  const truck = $('truck'), seq = $('seq');
  const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FADE = still ? 0 : 260, STEP = 72;
  const order = still ? [] : opening ? frames : [...frames].reverse();
  const fadeTo = (el, o, ms) => { el.style.transition = ms ? `opacity ${ms}ms ease` : 'none'; el.style.opacity = o; };
  if (!order.length) {
    fadeTo(truck, 0, FADE / 2); await wait(FADE / 2);
    land();
    fadeTo(truck, 1, FADE / 2); await wait(FADE / 2);
    truck.style.transition = '';
    return;
  }
  order.forEach(im => fadeTo(im, 0, 0));
  seq.replaceChildren(...order);
  void seq.offsetWidth;
  fadeTo(truck, 0, 0);
  fadeTo(order[0], 1, 0);
  await wait(STEP);
  for (let i = 1; i < order.length; i++) {
    fadeTo(order[i], 1, 0);
    fadeTo(order[i - 1], 0, 0);
    await wait(STEP);
  }
  land();
  const last = order[order.length - 1];
  fadeTo(truck, 1, 0);
  fadeTo(last, 0, 0);
  truck.style.transition = '';
  seq.replaceChildren();
}

async function openUp(key, view, scroll = true) {
  const o = OPENERS[key], id = view && RENDERS.views[view] ? view : targetOf(key), v = RENDERS.views[id];
  if (busy || !v) return;
  busy = true;
  state.hover = null;
  $('tip').hidden = true;
  manualUntil = Date.now() + 5000;
  paintSpots();
  const arrived = whenSet(v.key);
  if (spinning()) await turnTo(fromFrame(id));
  $('frame').classList.add('loading');
  await arrived;
  ready(v);
  $('frame').classList.remove('loading');
  await play(v.els.open || [], true, () => setView(id, true));
  busy = false;
  paintSpots();
  if (scroll && isWide()) $('sec-' + o.sec).scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function closeUp(key) {
  const id = state.view, v = RENDERS.views[id];
  if (busy || !v || !SPIN) return;
  busy = true;
  manualUntil = Date.now() + 5000;
  paintSpots();
  // Back to the turnaround: where the animation started, or for the view from
  // underneath, the rear three-quarter frame nearest its angle.
  const back = id === 'under' ? nearestFrame(140) : fromFrame(id);
  await play(key === 'under' ? [] : v.els.open || [], false, () => { state.frame = back; setView('outside', true); });
  busy = false;
  paintSpots();
}

let toastTimer = 0;
function toast(html) {
  const t = $('toast');
  t.innerHTML = html;
  t.classList.add('on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove('on'), 1600);
}

/* --------------------------------------------------------------- the panel */
const TICK = '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="M2.5 7.5 L5.6 10.4 L11.5 3.8" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHEV = '<svg viewBox="0 0 18 18" aria-hidden="true"><path d="M4 7 L9 12 L14 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function priceText(p) {
  if (state.sel[p.id]) return money(optOf(p.id, state.sel[p.id]).price);
  return p.opts.length > 1 ? `from ${money(priceFrom(p))}` : money(p.opts[0].price);
}

function kitSelection(k) {
  const out = {};
  for (const [pid, zs] of Object.entries(k.covers)) {
    const o = PIECE[pid].opts.find(x => zs.length === x.zones.length && zs.every(z => x.zones.includes(z)));
    out[pid] = o ? o.id : defOpt(pid);
  }
  return out;
}

// A kit is complete when everything it covers is picked, with at least the
// kit's coverage. Kit cards toggle: a complete kit's parts come off again.
function kitComplete(k) {
  return Object.entries(k.covers).every(([pid, zs]) => state.sel[pid] && zs.every(z => optOf(pid, state.sel[pid]).zones.includes(z)));
}
// Complete kits show as picked, except one inside a bigger complete kit (the
// Exterior Kit inside the Ultimate), which shows as included in it.
function kitStates() {
  const done = KITS.filter(kitComplete);
  const size = k => Object.keys(k.covers).length;
  const out = {};
  for (const k of KITS) {
    const outer = done.includes(k) && done.find(o => o !== k && size(o) > size(k) && Object.keys(k.covers).every(pid => pid in o.covers));
    out[k.id] = !done.includes(k) ? null : outer ? outer : 'on';
  }
  return out;
}
// Picking a kit shows what it covers: the Vault and Interior kits open the
// truck (the opener and the view it lands on); the others close it again.
const KIT_OPENS = { vault: ['vault', 'bed'], interior: ['doors', 'cabin'] };

async function toggleKit(k) {
  const adding = !kitComplete(k);
  if (!adding) for (const pid of Object.keys(k.covers)) delete state.sel[pid];
  else {
    // Someone who picked the Terrestrial rockers, then a kit: keep that as a swap.
    if (state.sel.rockers === 'terr' && k.covers.rockers) state.terrSwap = true;
    Object.assign(state.sel, kitSelection(k));
  }
  state.open = null;
  state.cart = false;
  render();
  if (!adding || busy) return;
  const show = KIT_OPENS[k.id];
  if (!show) {
    if (SPIN && !spinning()) closeUp(state.view === 'under' ? 'under' : openerOf(state.view));
    return;
  }
  if (state.view === show[1] || !RENDERS.views[show[1]]) return;
  // From another open view, close it first, so the truck turns and opens
  // rather than jumping.
  if (SPIN && !spinning()) await closeUp(state.view === 'under' ? 'under' : openerOf(state.view));
  if (spinning()) openUp(show[0], show[1], false);
  else setView(show[1], true);
}

function renderSections() {
  const extras = kitExtras(state.sel, sol);
  const kits = kitStates();
  $('sections').innerHTML = SECTIONS.map(s => {
    if (s.id === 'kits') {
      return `<section class="sec" id="aoa-sec-kits" data-view="${s.view}"><div class="sec-head"><h2>${s.title}</h2></div>
        <p class="sec-note">Or pick parts one by one below. Either way you pay the lowest total. Tap a kit again to take it off.</p>
        <div class="kits">${KITS.map(k => {
          const parts = kitParts(k), price = kitPrice(k), st = kits[k.id], inside = st && st !== 'on' ? st : null;
          const note = inside ? `Included in the ${esc(inside.name)}` : parts > price ? `Save ${money(parts - price)}` : 'No saving at these prices';
          if (k.out && !st) return `<button class="kit" type="button" disabled><b>${esc(k.name)}</b><small>${esc(k.cov)}</small><em>Unavailable</em></button>`;
          return `<button class="kit${inside ? ' inc' : ''}" type="button" data-kit="${k.id}" aria-pressed="${!!st}"><span class="tick">${TICK}</span><b>${esc(k.name)}</b><small>${esc(k.cov)}</small>
            <em>${money(price)}<s>${money(parts)}</s></em><strong>${note}</strong></button>`;
        }).join('')}</div>${rockersInKit() ? terrSwapBox() : ''}${ultBox()}</section>`;
    }
    const n = s.parts.filter(pid => state.sel[pid]).length;
    const shut = !!state.shut[s.id];
    return `<section class="sec${shut ? ' shut' : ''}" id="aoa-sec-${s.id}" data-view="${s.view}"><div class="sec-head"><h2><button class="sec-btn" type="button" data-act="fold" data-s="${s.id}" aria-expanded="${!shut}" aria-controls="aoa-parts-${s.id}">${s.title}${CHEV}</button></h2><span>${n ? `${n} picked` : ''}</span></div>
      <ul class="parts" id="aoa-parts-${s.id}"${shut ? ' hidden' : ''}>${s.parts.map(pid => partRow(PIECE[pid], extras)).join('')}</ul></section>`;
  }).join('');
}

function partRow(p, extras) {
  const on = !!state.sel[p.id];
  const out = !on && pieceOut(p.id);
  const inc = !on && extras.includes(p.id);
  const open = state.open === p.id;
  const o = on ? optOf(p.id, state.sel[p.id]) : optOf(p.id, defOpt(p.id));
  const tags = (inc ? '<span class="ptag ptag-inc">In your kit</span>' : '') +
    `<span class="ptag ${EASE_TAG[p.ease]}" title="${EASE_NOTE[p.ease]}">${p.ease}</span>`;
  const sub = on && p.opts.length > 1 ? o.label : p.sub;
  const img = IMG[o.img || p.id];
  return `<li class="part${inc ? ' is-inc' : ''}${out ? ' is-out' : ''}${state.hover === p.id ? ' is-hl' : ''}" data-p="${p.id}">
    <div class="part-row">
      <button class="pick" type="button" aria-pressed="${on}" data-act="toggle" data-p="${p.id}"${out ? ' disabled' : ''}>
        <span class="tick">${TICK}</span>
        <span class="ph">${img ? `<img src="${img}" alt="">` : ''}</span>
        <span><span class="pname">${esc(p.name)}${tags}</span><span class="psub">${esc(sub)}</span></span>
        <span class="pprice">${out ? 'Unavailable' : priceText(p)}</span>
      </button>
      <button class="more" type="button" aria-expanded="${open}" aria-controls="aoa-d-${p.id}" aria-label="Details for ${esc(p.name)}" data-act="more" data-p="${p.id}">${CHEV}</button>
    </div>
    ${open ? renderDetail(p, o) : ''}
  </li>`;
}

// What the install ratings mean (Pete's ratings, 2026-10-10).
const EASE_TAG = { Easy: 'ptag-easy', Tricky: 'ptag-mod', Challenging: 'ptag-hard' };
// Pete's wording, 2026-10-10.
const EASE_NOTE = {
  Easy: 'Easy: straightforward to install, typically smaller sections that don’t need any manipulation',
  Tricky: 'Tricky: very doable, but if you buy a kit, do the easy pieces first to get some practice',
  Challenging: 'Challenging: large or sometimes tedious, repetitive pieces to install. A second pair of hands can help',
};

const ROOT = String(CFG.root || '/').replace(/\/$/, '');

function renderDetail(p, o) {
  const img = IMG[o.img || p.id];
  const t = o.time || p.time;
  const mins = t[0] === t[1] ? `${t[0]} min` : (t[1] <= 15 ? 'under 15 min' : `${t[0]}–${t[1]} min`);
  const guide = o.guide || p.guide;
  let opts = p.opts.length > 1 ? `<fieldset class="opts"><legend>Choose</legend><div class="opt-row">${p.opts.map(x => `
      <label class="opt"><input type="radio" name="o-${p.id}" value="${x.id}" data-act="opt" data-p="${p.id}"${(state.sel[p.id] || defOpt(p.id)) === x.id ? ' checked' : ''}${x.out ? ' disabled' : ''}>
      <span>${esc(x.label)}<small>${x.out ? 'Unavailable' : money(x.price)}</small></span></label>`).join('')}</div></fieldset>` : '';
  if (p.id === 'rockers') {
    opts = rockersInKit() ? terrSwapBox() : opts + `<p class="fit">Trucks come from the factory with standard rockers. If yours has Tesla’s Terrestrial Armor Package, choose that instead.</p>`;
  }
  if (p.truck) {
    const Q = QUESTIONS[p.truck];
    opts += `<fieldset class="opts ask"><legend>${p.truck === 'roof' ? 'Which roof trims does your truck have?' : 'Which vault front wall does your truck have?'}</legend><div class="opt-row">${Q.options.map(x => `
      <label class="opt"><input type="radio" name="d-q-${p.truck}" value="${x.id}" data-act="answer" data-q="${p.truck}"${state.answers[p.truck] === x.id ? ' checked' : ''}>
      <span>${esc(x.label)}<small>${esc(x.note)}</small></span></label>`).join('')}</div>
      <details data-keep="d-q-${p.truck}"${state.keepOpen['d-q-' + p.truck] ? ' open' : ''}><summary>How do I tell?</summary><img src="${IMG[Q.img]}" alt="${esc(Q.imgAlt)}"></details></fieldset>`;
  }
  return `<div class="detail" id="aoa-d-${p.id}">
    ${img ? `<img src="${img}" alt="${esc(p.name)} fitted to a Cybertruck">` : ''}
    <p>${esc(p.blurb)}</p>
    ${o.note ? `<p class="fit">${esc(o.note)}</p>` : ''}
    ${p.fit ? `<p class="fit"><b>Fit check:</b> ${esc(p.fit)}</p>` : ''}
    ${opts}
    <div class="meta"><span>${EASE_NOTE[p.ease]} · ${mins} to fit</span>
    <a href="${ROOT}/pages/${guide}" target="_blank" rel="noopener">Watch the install guide</a></div>
  </div>`;
}

function renderFoot() {
  const btn = $('goBtn');
  if (!sol) {
    $('footTotal').innerHTML = `<b>${money(0)}</b>`;
    $('footMsg').className = 'foot-msg idle';
    $('footMsg').textContent = 'Tap the truck or pick parts to start.';
    btn.disabled = true;
    return;
  }
  btn.disabled = false;
  btn.textContent = 'Review';
  $('footTotal').innerHTML = `<b>${money(sol.total)}</b>${sol.saving > 0.005 ? `<s>${money(sol.baseline)}</s>` : ''}`;
  const msg = $('footMsg');
  if (tier && !tier.done) {
    const steps = Array.from({ length: 5 }, (_, i) => `<i class="${i < tier.n ? 'on' : ''}"></i>`).join('');
    msg.className = 'foot-msg hot';
    msg.innerHTML = `<span class="mini-steps" aria-hidden="true">${steps}</span>${tier.need} more part${tier.need > 1 ? 's' : ''} → ${tier.pct}% off your bundle`;
  } else if (sol.saving > 0.005) {
    msg.className = 'foot-msg';
    msg.textContent = `You save ${money(sol.saving)} (${Math.round(sol.saving / sol.baseline * 100)}%)`;
  } else {
    msg.className = 'foot-msg idle';
    msg.textContent = `${Object.keys(state.sel).length} part${Object.keys(state.sel).length > 1 ? 's' : ''} picked`;
  }
}

/* --------------------------------------------------------- order summary */
function neededQuestions() {
  const q = new Set();
  if (!sol) return [];
  if (state.sel.roof) q.add('roof');
  if (state.sel['vault-walls']) q.add('vault');
  for (const k of sol.kits) (k.needs || []).forEach(n => q.add(n));
  return ['vault', 'roof'].filter(x => q.has(x));
}

function line(title, sub, price, was) {
  return `<li class="line"><span><span class="lt">${esc(title)}</span>${sub ? `<span class="ls">${esc(sub)}</span>` : ''}</span>
    <span class="lp">${was && was - price > 0.005 ? `<s>${money(was)}</s>` : ''}<b>${money(price)}</b></span></li>`;
}

function renderSummary() {
  if (!state.sheet) return;
  const pids = Object.keys(state.sel);
  const extras = kitExtras(state.sel, sol);
  if (!sol) { $('sumBody').innerHTML = '<p class="empty">Nothing picked yet.</p>'; return; }
  const qs = neededQuestions();
  const unanswered = qs.filter(q => !state.answers[q]);
  let html = '';
  if (qs.length) {
    html += `<div class="qs"><p class="qs-title">${['One quick question', 'Two quick questions', 'Three quick questions'][qs.length - 1]} so we send the right pieces</p>`;
    for (const q of qs) {
      const Q = QUESTIONS[q];
      html += `<fieldset class="q"><legend>${Q.legend}</legend><div class="opt-row">${Q.options.map(o => `
        <label class="opt"><input type="radio" name="q-${q}" value="${o.id}" data-act="answer" data-q="${q}"${state.answers[q] === o.id ? ' checked' : ''}>
        <span>${esc(o.label)}<small>${esc(o.note)}</small></span></label>`).join('')}</div>
        <details data-keep="q-${q}"${state.keepOpen['q-' + q] ? ' open' : ''}><summary>How do I tell?</summary><img src="${IMG[Q.img]}" alt="${esc(Q.imgAlt)}"></details></fieldset>`;
    }
    html += '</div>';
  }
  if (rockersInKit()) html += terrSwapBox();
  html += '<ul class="lines">';
  for (const k of sol.kits) html += line(k.name, k.sub, kitPrice(k), kitParts(k));
  if (sol.pool.length) {
    if (sol.rate > 0) html += line(`Bundle of ${sol.pool.length} · ${Math.round(sol.rate * 100)}% off`, joinNames(sol.pool.map(skuName)), sol.poolCost, sol.poolList);
    else sol.pool.forEach(s => { html += line(skuName(s), sol.kits.length ? '' : 'Full price until you have 3 parts', s.price, null); });
  }
  if (sol.add.length) html += line(`Ultimate Kit add-ons · ${RULES.addon}% off`, joinNames(sol.add.map(skuName)), sol.addCost, sol.addList);
  html += '</ul>';
  html += ultBox();
  if (tier) html += renderMeter();
  if (extras.length) html += `<p class="kitnote">Your kit also includes ${esc(joinNames(extras.map(pid => PIECE[pid].name)))}. ${extras.length > 1 ? 'They are' : 'It is'} shown on the truck.</p>`;
  if (!sol.kits.some(k => k.prep)) {
    html += `<label class="prep"><input type="checkbox" data-act="prep"${state.prep ? ' checked' : ''}>
      <span><b>Installation kit</b>Alcohol spray, microfiber cloth and squeegee. Free with every full kit.</span>
      <span class="lp"><b>${money(INSTALL_KIT.price)}</b></span></label>`;
  }
  html += `<div class="total"><span>Total</span><b>${money(sol.total)}</b></div>`;
  if (sol.saving > 0.005) html += `<p class="save">You save ${money(sol.saving)} (${Math.round(sol.saving / sol.baseline * 100)}%) compared with buying each piece on its own.</p>`;
  if (nudge) html += renderNudge(nudge);
  html += `<button class="cta" type="button" data-act="cart"${unanswered.length || adding ? ' disabled' : ''}${adding ? ' aria-busy="true"' : ''}>${adding ? 'Adding…' : `Add to cart · ${money(sol.total)}`}</button>`;
  if (unanswered.length) html += `<p class="cta-why">Answer the question${unanswered.length > 1 ? 's' : ''} above first.</p>`;
  if (state.cartErr) html += `<p class="cart-err" role="alert">${esc(state.cartErr)}</p>`;
  html += `<ul class="fyi"><li>${timeText(fitTime(pids.concat(extras)))} Every part has its own video guide.</li><li>Order by 2pm Central and it usually ships the same day.</li></ul>`;
  html += `<details class="howprice" data-keep="howprice"${state.keepOpen.howprice ? ' open' : ''}><summary>How we price this</summary>
    <p>We compare every way of buying what you picked and show you the cheapest.</p>
    <p><b>Kits:</b> Ultimate, Exterior, Vault and Interior, each priced below its parts.<br>
    <b>Bundle:</b> ${RULES.t3}% off 3 parts, ${RULES.t4}% off 4, ${RULES.t5}% off 5 or more.<br>
    <b>With the Ultimate Kit:</b> ${RULES.addon}% off the five add-ons it doesn't include: lower doors, baffle trims, frunk interior, Terrestrial trims and the rear bumper add-on.</p></details>`;
  $('sumBody').innerHTML = html;
}

function renderMeter() {
  const n = Math.min(tier.n, 5);
  const bars = Array.from({ length: 5 }, (_, i) => `<i class="${i < n ? 'on' : ''}"></i>`).join('');
  const labels = [1, 2, 3, 4, 5].map(i => `<span class="${tier.n >= i && i >= 3 ? 'on' : ''}">${i < 3 ? '' : `${tierPct(i)}%${i === 5 ? '+' : ''}`}</span>`).join('');
  let head, sub = '';
  if (tier.done) head = `Your bundle gets our biggest discount: <b>${tier.pct}% off</b>.`;
  else {
    head = `Add ${tier.need} more part${tier.need > 1 ? 's' : ''} and your whole bundle is <b>${tier.pct}% off</b>.`;
    if (tier.extra > 0.005) sub = `That's another ${money(tier.extra)} off the parts you've already picked.`;
  }
  const chips = adds.map(a => `<button type="button" data-act="add" data-p="${a.pid}">+ ${esc(PIECE[a.pid].name)} · ${a.delta < -0.005 ? `<em>total −${money(-a.delta)}</em>` : `+${money(a.delta)}`}</button>`).join('');
  return `<div class="meter"><div class="meter-head">${head}</div>
    <div class="steps" aria-hidden="true">${bars}</div><div class="steps-l" aria-hidden="true">${labels}</div>
    ${sub ? `<p>${sub}</p>` : ''}${chips ? `<p>Quick adds, with the effect on your total:</p><div class="adds">${chips}</div>` : ''}</div>`;
}

function renderNudge(n) {
  const names = joinNames(n.pids.map(pid => PIECE[pid].name));
  let msg;
  if (n.delta < -0.005) msg = `Add ${names} and your total goes <b>down</b> by ${money(-n.delta)}.`;
  else if (n.delta <= 0.005) msg = `Add ${names} at no extra cost.`;
  else msg = `Add ${names} for <b>${money(n.delta)}</b> more. On ${n.pids.length > 1 ? 'their' : 'its'} own, ${n.pids.length > 1 ? 'they cost' : 'it costs'} ${money(n.list)}.`;
  if (n.kitWins) msg += ` That completes the ${n.kit.name}.`;
  return `<div class="nudge"><p>${msg}</p><button type="button" data-act="nudge">Add ${n.pids.length > 1 ? 'them' : 'it'}</button></div>`;
}

// Everything in the cart in one request: the kits (in the variant the truck
// questions picked), the parts, the add-ons and the installation kit. Each
// line carries a hidden _source property so builder orders can be told apart;
// a kit whose standard rockers are to be swapped says so on its line. Checkout
// applies the bundle and add-on discounts itself (Shopify automatic discounts).
let adding = false;
async function addToCart() {
  if (adding || !sol) return;
  const a = state.answers;
  const vidOf = s => {
    if (s.extra) return s.vid;
    const o = optOf(s.pid, s.oid);
    return typeof o.vid === 'string' ? o.vid : o.vid[PIECE[s.pid].truck === 'vault' ? a.vault : a.roof];
  };
  const props = { _source: 'Armor builder' };
  const items = [];
  let swapped = false;
  for (const k of sol.kits) {
    const p = { ...props };
    if (state.terrSwap && k.covers.rockers && !swapped) { p.Rockers = 'Swap for the Terrestrial Armor Package kit (no cost)'; swapped = true; }
    items.push({ id: Number(k.vid(a)), quantity: 1, properties: p });
  }
  for (const s of [...sol.pool, ...sol.add]) items.push({ id: Number(vidOf(s)), quantity: 1, properties: props });
  if (sol.prepCost) items.push({ id: Number(INSTALL_KIT.vid), quantity: 1, properties: props });

  adding = true;
  state.cartErr = '';
  renderSummary();
  const cartType = window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartType;
  try {
    const res = await fetch(`${CFG.cartAdd || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: ['mini-cart'] }),
    });
    const added = await res.json();
    if (!res.ok) throw new Error(added.description || added.message || 'Something went wrong adding these to your cart.');
    if (cartType === 'page') { window.location.href = CFG.cartUrl || '/cart'; return; }
    const cart = await (await fetch(`${CFG.cartUrl || '/cart'}.js`)).json();
    adding = false;
    openSheet(false);
    // The same events Focal's own product form fires, so the cart count, the
    // drawer and anything else listening behave as usual.
    root.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: added.items && added.items[0] } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    cart.sections = added.sections;
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: cartType !== 'page' } }));
    toast('Added to your cart');
  } catch (err) {
    adding = false;
    state.cartErr = err.message || 'Something went wrong adding these to your cart.';
    renderSummary();
  }
}

function openSheet(on) {
  state.sheet = on;
  $('sheet').hidden = !on;
  $('sheetBack').hidden = !on;
  if (on) { renderSummary(); $('sheet').querySelector('.sheet-head button').focus(); }
  else $('goBtn').focus();
}

/* --------------------------------------------------------------- render */
function render() {
  if (!adding) state.cartErr = '';
  sol = solve(state.sel, state.prep);
  // They told us their truck has the Terrestrial package, but the rockers are
  // no longer part of a kit (the bundle came out cheaper): sell the Terrestrial
  // kit itself, rather than the standard one to be swapped.
  if (state.terrSwap && state.sel.rockers === 'std' && !rockersInKit()) {
    state.sel.rockers = 'terr';
    state.terrSwap = false;
    sol = solve(state.sel, state.prep);
  }
  tier = tierInfo(sol);
  adds = tier && !tier.done ? addCandidates(state.sel, sol, state.prep).slice(0, 3) : [];
  nudge = kitNudge(state.sel, sol, state.prep);
  const scroller = isWide() ? $('panelScroll') : document.scrollingElement;
  const keep = scroller.scrollTop;
  renderSections();
  scroller.scrollTop = keep;
  paintTruck();
  paintThumbs();
  renderFoot();
  renderSummary();
  observeSections();
}

/* ----------------------------------------------------------------- events */
// Parts with something to settle for the customer's truck.
const ASK_TOAST = {
  rockers: 'Terrestrial package? See the list',
  roof: 'With or without access panels? See the list',
  'vault-walls': 'Flat or ribbed front wall? See the list',
};
const asks = pid => pid === 'rockers' || (PIECE[pid].truck && !state.answers[PIECE[pid].truck]);
const rockersInKit = () => !!sol && sol.kits.some(k => k.covers.rockers);
// The add-ons the Ultimate Kit doesn't include, 30% off with it. Terrestrial
// isn't offered here: with a kit, it's the no-cost swap in the box above.
const ULT_ADDS = [
  { pid: 'doors', name: 'Lower doors' },
  { pid: 'baffle', name: 'Baffle trims' },
  { pid: 'frunk', name: 'Frunk interior', oid: 'full', sub: 'Walls and floor' },
  { pid: 'rear-bumper', name: 'Rear bumper underside', oid: 'extreme', base: 'full', sub: 'The rear bumper add-on' },
];
const ultOn = a => a.base ? state.sel[a.pid] === a.oid : !!state.sel[a.pid];
const ultList = a => a.pid === 'rear-bumper' ? REAR_ADDON.price : optOf(a.pid, a.oid || defOpt(a.pid)).price;
const ultOut = a => a.pid === 'rear-bumper' ? REAR_ADDON.out : pieceOut(a.pid);
function ultBox() {
  if (!sol || !sol.kits.some(k => k.unlocks)) return '';
  const rows = ULT_ADDS.filter(a => !ultOut(a)).map(a => {
    const on = ultOn(a), list = ultList(a);
    return `<li><span>${esc(a.name)}<small>${a.sub ? esc(a.sub) + ' · ' : ''}<s>${money(list)}</s><em>${money(r2(list * (1 - RULES.addon / 100)))}</em></small></span>
      <button type="button" data-act="ult" data-p="${a.pid}" aria-pressed="${on}">${on ? 'Added' : '+ Add'}</button></li>`;
  }).join('');
  if (!rows) return '';
  return `<div class="ult"><p class="ult-head">Your Ultimate Kit unlocks <b>${RULES.addon}% off</b> these add-ons</p>
    <p>They aren't in the kit. Add them with it and they're ${RULES.addon}% off too.</p><ul>${rows}</ul></div>`;
}
function toggleUlt(pid) {
  const a = ULT_ADDS.find(x => x.pid === pid);
  if (!a) return;
  if (a.base) state.sel[pid] = ultOn(a) ? a.base : a.oid;
  else if (state.sel[pid]) delete state.sel[pid];
  else state.sel[pid] = a.oid || defOpt(pid);
  state.cartErr = '';
  render();
  flash(pid);
}

const terrSwapBox = () => `<label class="swap"><input type="checkbox" data-act="terr"${state.terrSwap ? ' checked' : ''}>
  <span><b>My truck has Tesla’s Terrestrial Armor Package</b>Swap the standard rockers in my kit for the Terrestrial kit, at no cost.</span></label>`;

function toggle(pid, fromTruck) {
  const adding = !state.sel[pid];
  if (adding && pieceOut(pid)) { if (fromTruck) toast(`${esc(PIECE[pid].name)} <span>is unavailable right now</span>`); return; }
  if (adding) {
    state.sel[pid] = defOpt(pid);
    if ((PIECE[pid].opts.length > 1 || PIECE[pid].truck) && (!fromTruck || asks(pid))) state.open = pid;
  } else {
    delete state.sel[pid];
    if (state.open === pid) state.open = null;
  }
  state.cart = false;
  if (!fromTruck && BEST[pid] !== state.view) setView(BEST[pid], true);
  render();
  flash(pid);
  if (!fromTruck && spinning()) {
    const f = bestFrame(pid);
    if (f !== null && f !== state.frame) turnTo(f);
  }
  if (fromTruck && adding && asks(pid)) toast(`Added: ${esc(PIECE[pid].name)} <span>${ASK_TOAST[pid]}</span>`);
  else if (fromTruck) toast(`${adding ? 'Added' : 'Removed'}: ${esc(PIECE[pid].name)} <span>${adding ? priceText(PIECE[pid]) : ''}</span>`);
}

function stepView(dir) {
  const i = THUMBS.findIndex(v => v.id === thumbOf(state.view));
  setView(THUMBS[(i + dir + THUMBS.length) % THUMBS.length].id, true);
}

root.addEventListener('click', e => {
  const t = e.target.closest('[data-act], [data-kit], [data-open], [data-close], .thumb');
  if (!t) return;
  if (busy && (t.classList.contains('thumb') || t.dataset.open || t.dataset.close || t.dataset.act === 'prev' || t.dataset.act === 'next')) return;
  if (t.classList.contains('thumb')) return setView(t.dataset.view, true);
  if (t.dataset.kit) return toggleKit(KITS.find(x => x.id === t.dataset.kit));
  if (t.dataset.open) return openUp(t.dataset.open);
  if (t.dataset.close) return closeUp(t.dataset.close);
  const act = t.dataset.act;
  if (act === 'toggle') return toggle(t.dataset.p, false);
  if (act === 'fold') { state.shut[t.dataset.s] = !state.shut[t.dataset.s]; render(); return; }
  if (act === 'more') { state.open = state.open === t.dataset.p ? null : t.dataset.p; render(); return; }
  if (act === 'prev') return stepView(-1);
  if (act === 'next') return stepView(1);
  if (act === 'review') return openSheet(true);
  if (act === 'close') return openSheet(false);
  if (act === 'add') { state.sel[t.dataset.p] = defOpt(t.dataset.p); state.cart = false; render(); return; }
  if (act === 'nudge' && nudge) { Object.assign(state.sel, nudge.picks); state.cart = false; render(); return; }
  if (act === 'ult') return toggleUlt(t.dataset.p);
  if (act === 'cart') return addToCart();
});

root.addEventListener('change', e => {
  const t = e.target;
  if (t.dataset.act === 'opt') {
    state.sel[t.dataset.p] = t.value; state.cart = false;
    if (RENDERS.views.under && optOf(t.dataset.p, t.value).zones.includes('under')) setView('under', true);
    render(); flash(t.dataset.p);
  }
  if (t.dataset.act === 'answer') {
    state.answers[t.dataset.q] = t.value;
    state.cart = false; render();
  }
  if (t.dataset.act === 'terr') { state.terrSwap = t.checked; state.cart = false; render(); }
  if (t.dataset.act === 'prep') { state.prep = t.checked; state.cart = false; render(); }
});
document.addEventListener('keydown', e => { if (e.key === 'Escape' && state.sheet) openSheet(false); });
root.addEventListener('toggle', e => {
  const d = e.target;
  if (d.dataset && d.dataset.keep) state.keepOpen[d.dataset.keep] = d.open;
}, true);

// Hover and tap on the truck itself; on Outside, drag to turn it.
const frame = $('frame');
let down = null, swiped = false, drag = null;
const onSpot = e => !!e.target.closest('.spot, .spot-close');
frame.addEventListener('pointermove', e => {
  if (busy) return;
  if (!drag && onSpot(e)) {
    if (state.hover) { state.hover = null; paintTruck(); }
    $('tip').hidden = true;
    frame.classList.remove('hover');
    return;
  }
  if (drag) {
    // About 30 frames across the picture's width: a full turn in one long drag.
    const per = frame.getBoundingClientRect().width / 30;
    const step = Math.round((e.clientX - drag.x) / per);
    if (step) drag.moved = true;
    const n = SPIN.frames.length;
    const f = ((drag.f + SPIN.dir * step) % n + n) % n;
    if (f !== state.frame) showFrame(f);
    if (drag.moved) {
      $('tip').hidden = true;
      if (!dragged) { dragged = true; $('dragHint').hidden = true; }
      return;
    }
  }
  if (e.pointerType === 'touch') return;
  const pid = partAt(e.clientX, e.clientY);
  frame.classList.toggle('hover', !!pid);
  if (pid !== state.hover) {
    state.hover = pid;
    paintTruck();
    root.querySelectorAll('.part').forEach(li => li.classList.toggle('is-hl', li.dataset.p === pid));
  }
  const tip = $('tip');
  if (!pid) { tip.hidden = true; return; }
  const r = frame.getBoundingClientRect();
  const p = PIECE[pid];
  const status = state.sel[pid] ? 'Added · click to remove' : kitExtras(state.sel, sol).includes(pid) ? 'In your kit' : 'Click to add';
  tip.innerHTML = `<b>${esc(p.name)}</b> · ${priceText(p)} · <span>${status}</span>`;
  tip.style.left = `${e.clientX - r.left}px`;
  tip.style.top = `${e.clientY - r.top}px`;
  tip.hidden = false;
});
frame.addEventListener('pointerleave', () => {
  if (drag) return;
  state.hover = null; $('tip').hidden = true; frame.classList.remove('hover'); paintTruck();
  root.querySelectorAll('.part.is-hl').forEach(li => li.classList.remove('is-hl'));
});
frame.addEventListener('pointerdown', e => {
  if (busy || onSpot(e)) return;
  down = { x: e.clientX, y: e.clientY };
  swiped = false;
  if (spinning() && e.button === 0) {
    clearInterval(turnTimer);
    drag = { x: e.clientX, f: state.frame, moved: false };
    try { frame.setPointerCapture(e.pointerId); } catch (err) { /* already released */ }
    frame.classList.add('grabbing');
  }
});
const endDrag = () => { drag = null; frame.classList.remove('grabbing'); };
frame.addEventListener('pointercancel', () => { endDrag(); down = null; });
frame.addEventListener('pointerup', e => {
  const turned = drag && drag.moved;
  endDrag();
  if (!down) return;
  const dx = e.clientX - down.x, dy = e.clientY - down.y;
  down = null;
  if (turned) return;
  if (!spinning() && Math.abs(dx) > 50 && Math.abs(dy) < 40) { swiped = true; stepView(dx < 0 ? 1 : -1); return; }
  if (Math.abs(dx) > 8 || Math.abs(dy) > 8) return;
  const pid = partAt(e.clientX, e.clientY);
  if (pid) toggle(pid, true);
});
// Arrow keys turn the truck when it has focus.
frame.addEventListener('keydown', e => {
  if (busy || !spinning() || (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft')) return;
  e.preventDefault();
  clearInterval(turnTimer);
  const n = SPIN.frames.length;
  showFrame(((state.frame + SPIN.dir * (e.key === 'ArrowRight' ? 1 : -1)) % n + n) % n);
});

$('thumbs').addEventListener('keydown', e => {
  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
  stepView(e.key === 'ArrowRight' ? 1 : -1);
  $('tab-' + thumbOf(state.view)).focus();
});

// As the options scroll, the truck turns to the section being read.
// Only a real scroll counts: every redraw of the list re-observes the
// sections, and the observer reports where they all are straight away, which
// used to swing the truck to whatever section sat near the top.
let sectionObserver = null, lastScroll = 0;
const noteScroll = () => { lastScroll = Date.now(); };
$('panelScroll').addEventListener('scroll', noteScroll, { passive: true });
window.addEventListener('scroll', noteScroll, { passive: true });
function observeSections() {
  if (sectionObserver) sectionObserver.disconnect();
  if (!('IntersectionObserver' in window)) return;
  sectionObserver = new IntersectionObserver(entries => {
    if (Date.now() < manualUntil || Date.now() - lastScroll > 300) return;
    const hit = entries.filter(en => en.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
    if (hit) setView(hit.target.dataset.view, false);
  }, { root: isWide() ? $('panelScroll') : null, rootMargin: isWide() ? '-10% 0px -60% 0px' : '-45% 0px -40% 0px' });
  root.querySelectorAll('.sec').forEach(s => sectionObserver.observe(s));
}
matchMedia('(min-width: 1000px)').addEventListener('change', observeSections);

renderThumbs();
buildView();
render();
})();
